import { useState, useEffect } from 'react';
import { FileText, Download, Lock, Camera, Check, Wine, X, Image as ImageIcon } from 'lucide-react';
import nhost from '../nhost';
import { avg, sweDate } from '../helpers';
import { awardPoints } from '../points';
import WineModal from './WineModal';
import WineDetail from './WineDetail';
import { pushBack, popBack } from '../historyStack';

const SESSIONS_QUERY = `
  query {
    sessions(
      order_by: { created_at: desc }
    ) {
      id food created_at event_id wines_visible completed
      profile { id display_name }
      event { id event_date event_time message cost event_type event_hosts { profile_id profile { id display_name phone } } rsvps { user_id status profile { display_name } } }
      recipes { id title url }
      payments { id user_id self_reported_at confirmed_by_host_at }
      wines(order_by: { created_at: asc }) {
        id name grape type country region vintage profile profile_source price packaging created_by created_at image_url source_url interests { id user_id profile { id display_name avatar_url } }
        session { id host_id food event_id profile { id display_name } event { event_type } }
        ratings { id rating comment user_id profile { display_name } }
      }
      session_comments(order_by: { created_at: asc }) {
        id content created_at user_id
        profile { display_name }
      }
      session_photos(order_by: { created_at: asc }) {
        id photo_url created_at user_id
      }
    }
  }
`;

function Stars({ rating, size = 13 }) {
  return (
    <span style={{ position: 'relative', display: 'inline-block', fontSize: size, letterSpacing: 2, lineHeight: 1 }}>
      <span style={{ color: 'rgba(60,20,30,.18)' }}>★★★★★</span>
      <span style={{ position: 'absolute', left: 0, top: 0, overflow: 'hidden', whiteSpace: 'nowrap', color: 'var(--gold)', width: `${rating / 5 * 100}%` }}>★★★★★</span>
    </span>
  );
}

export default function Sessions({ profile, onViewMember, getCached, setCache, invalidateCache }) {
  const [sessions, setSessions] = useState([]);
  const [addingToSession, setAddingToSession] = useState(null);
  const [detail, setDetail] = useState(null);
  const [allWines, setAllWines] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [updating, setUpdating] = useState(null);
  const [eventFilter, setEventFilter] = useState('tasting');
  const [editingFood, setEditingFood] = useState({});
  const [newRecipe, setNewRecipe] = useState({});
  const [savingFood, setSavingFood] = useState(null);
  const [editingCost, setEditingCost] = useState({});
  const [savingCost, setSavingCost] = useState(null);
  const [swishPayee, setSwishPayee] = useState({});
  const [newComment, setNewComment] = useState({});
  const [addingComment, setAddingComment] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const [completingSession, setCompletingSession] = useState(null);
  const [adminMenuOpen, setAdminMenuOpen] = useState(null);

  useEffect(() => { fetchSessions(); }, []);

  async function fetchSessions(force = false) {
    const cacheKey = 'sessions';
    if (!force) {
      const cached = getCached?.(cacheKey);
      if (cached) {
        setSessions(cached);
        setAllWines(cached.flatMap(s => s.wines));
        const foodMap = {};
        cached.forEach(s => { foodMap[s.id] = s.food || ''; });
        setEditingFood(foodMap);
        return;
      }
    }
    const { data } = await nhost.graphql.request(SESSIONS_QUERY);
    if (data) {
      setSessions(data.sessions);
      setAllWines(data.sessions.flatMap(s => s.wines));
      const foodMap = {};
      const costMap = {};
      data.sessions.forEach(s => {
        foodMap[s.id] = s.food || '';
        costMap[s.id] = s.event?.cost ? String(s.event.cost) : '';
      });
      setEditingFood(foodMap);
      setEditingCost(costMap);
      setCache?.(cacheKey, data.sessions);
    }
  }

  async function setSessionFlag(sessionId, field, value) {
    setUpdating(sessionId);
    await nhost.graphql.request(`
      mutation UpdateSession($id: uuid!, $changes: sessions_set_input!) {
        update_sessions_by_pk(pk_columns: { id: $id }, _set: $changes) { id }
      }
    `, { id: sessionId, changes: { [field]: value } });

    if (field === 'completed' && value === true) {
      // Poängtilldelning görs server-side med admin-rättigheter, eftersom Hasuras
      // insert-permission bara tillåter att skriva poäng till sig själv – annars
      // hade bara den som klickar "Avsluta provning" fått poäng.
      await fetch('/api/complete-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId }),
      }).catch(() => {});
      const session2 = sessions.find(s => s.id === sessionId);
      const hostName = session2?.profile?.display_name || 'Värden';
      const goingIds2 = session2?.event?.rsvps?.filter(r => r.status === 'going').map(r => r.user_id) || [];
      fetch('/api/push?action=send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: session2?.event?.event_type === 'wine_box' ? 'Vinlåda avslutad' : 'Provning avslutad',
          body: session2?.event?.event_type === 'wine_box' ? `${hostName}s vinlåda är avslutad. Se viner och betyg!` : `${hostName}s provning är avslutad. Se resultat och betyg!`,
          url: '/',
          userIds: goingIds2,
        }),
      }).catch(() => {});
    }

    if (field === 'wines_visible' && value === true) {
      const session = sessions.find(s => s.id === sessionId);
      const hostName = session?.profile?.display_name || 'Värden';
      const goingIds = session?.event?.rsvps?.filter(r => r.status === 'going').map(r => r.user_id) || [];
      fetch('/api/push?action=send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: 'Viner avslöjas!',
          body: `${hostName} har avslöjat vilka viner som ingick i provningen.`,
          url: '/',
          userIds: goingIds,
        }),
      }).catch(() => {});
    }

    invalidateCache?.('sessions');
    await fetchSessions(true);
    setUpdating(null);
  }

  async function saveFood(sessionId) {
    setSavingFood(sessionId);
    await nhost.graphql.request(`
      mutation UpdateFood($id: uuid!, $food: String) {
        update_sessions_by_pk(pk_columns: { id: $id }, _set: { food: $food }) { id }
      }
    `, { id: sessionId, food: editingFood[sessionId] || null });
    invalidateCache?.('sessions');
    await fetchSessions(true);
    setSavingFood(null);
  }

  async function addRecipe(sessionId) {
    const recipe = newRecipe[sessionId];
    if (!recipe?.url?.trim()) return;
    await nhost.graphql.request(`
      mutation AddRecipe($session_id: uuid!, $title: String, $url: String!) {
        insert_session_recipes_one(object: { session_id: $session_id, title: $title, url: $url }) { id }
      }
    `, { session_id: sessionId, title: recipe.title?.trim() || null, url: recipe.url.trim() });
    setNewRecipe(prev => ({ ...prev, [sessionId]: { title: '', url: '' } }));
    await fetchSessions();
  }

  async function deleteRecipe(recipeId) {
    await nhost.graphql.request(`
      mutation DeleteRecipe($id: uuid!) { delete_session_recipes_by_pk(id: $id) { id } }
    `, { id: recipeId });
    await fetchSessions();
  }

  async function saveCost(sessionId, eventId) {
    setSavingCost(sessionId);
    const cost = editingCost[sessionId] ? parseFloat(editingCost[sessionId]) : null;
    await nhost.graphql.request(`
      mutation UpdateEventCost($id: uuid!, $cost: numeric) {
        update_events_by_pk(pk_columns: { id: $id }, _set: { cost: $cost }) { id }
      }
    `, { id: eventId, cost });
    invalidateCache?.('sessions');
    invalidateCache?.('events');
    await fetchSessions(true);
    setSavingCost(null);
  }

  async function toggleSelfReport(sessionId, userId, reported) {
    await nhost.graphql.request(`
      mutation SelfReportPayment($session_id: uuid!, $user_id: uuid!, $at: timestamptz) {
        insert_session_payments_one(
          object: { session_id: $session_id, user_id: $user_id, self_reported_at: $at },
          on_conflict: { constraint: session_payments_session_id_user_id_key, update_columns: [self_reported_at] }
        ) { id }
      }
    `, { session_id: sessionId, user_id: userId, at: reported ? new Date().toISOString() : null });
    invalidateCache?.('sessions');
    await fetchSessions(true);
  }

  async function toggleHostConfirm(sessionId, userId, confirmed) {
    await fetch('/api/complete-session?action=confirm-payment', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sessionId, userId, hostId: profile.id, confirmed }),
    });
    invalidateCache?.('sessions');
    await fetchSessions(true);
  }

  function makeSwishLink(phone, amount, label) {
    let p = phone.replace(/[\s\-()\+]/g, '');
    if (p.startsWith('00')) p = '+' + p.substring(2);
    else if (p.startsWith('0')) p = '+46' + p.substring(1);
    else if (!p.startsWith('+')) p = '+46' + p;
    const data = {
      version: 1,
      payee: { value: p, editable: false },
      amount: amount ? { value: amount, editable: false } : { value: 0, editable: true },
      message: { value: label || 'Vinligan', editable: true },
    };
    return `swish://payment?data=${encodeURIComponent(JSON.stringify(data))}`;
  }

  async function addComment(sessionId) {
    const content = newComment[sessionId]?.trim();
    if (!content) return;
    setAddingComment(sessionId);
    await nhost.graphql.request(`
      mutation AddComment($session_id: uuid!, $user_id: uuid!, $content: String!) {
        insert_session_comments_one(object: { session_id: $session_id, user_id: $user_id, content: $content }) { id }
      }
    `, { session_id: sessionId, user_id: profile.id, content });
    const session = sessions.find(s => s.id === sessionId);
    const myComments = session?.session_comments?.filter(c => c.user_id === profile.id).length || 0;
    if (myComments < 3) await awardPoints(profile.id, 'session_comment', `${sessionId}_${myComments}`);
    setNewComment(prev => ({ ...prev, [sessionId]: '' }));
    invalidateCache?.('sessions');
    await fetchSessions(true);
    setAddingComment(null);
  }

  async function deleteComment(commentId) {
    await nhost.graphql.request(`
      mutation DeleteComment($id: uuid!) { delete_session_comments_by_pk(id: $id) { id } }
    `, { id: commentId });
    invalidateCache?.('sessions');
    await fetchSessions(true);
  }

  async function handlePhotoFile(sessionId, e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingPhoto(sessionId);
    try {
      const formData = new FormData();
      formData.append('image', file);
      const res = await fetch(`https://api.imgbb.com/1/upload?key=f6935b01a235980fdffacf05baca1614`, { method: 'POST', body: formData });
      const data = await res.json();
      if (data.success) {
        await addPhoto(sessionId, data.data.url);
        await awardPoints(profile.id, 'upload_photo', `${sessionId}_photo_${Date.now()}`);
      }
    } catch {}
    setUploadingPhoto(null);
    e.target.value = '';
  }

  async function addPhoto(sessionId, photoUrl) {
    await nhost.graphql.request(`
      mutation AddPhoto($session_id: uuid!, $user_id: uuid!, $photo_url: String!) {
        insert_session_photos_one(object: { session_id: $session_id, user_id: $user_id, photo_url: $photo_url }) { id }
      }
    `, { session_id: sessionId, user_id: profile.id, photo_url: photoUrl });
    invalidateCache?.('sessions');
    await fetchSessions(true);
  }

  async function deletePhoto(photoId) {
    await nhost.graphql.request(`
      mutation DeletePhoto($id: uuid!) { delete_session_photos_by_pk(id: $id) { id } }
    `, { id: photoId });
    invalidateCache?.('sessions');
    await fetchSessions(true);
  }

  function openLightbox(photos, index) {
    setLightbox({ photos, index });
  }

  function lightboxNext(e) {
    e.stopPropagation();
    setLightbox(lb => lb && { ...lb, index: Math.min(lb.index + 1, lb.photos.length - 1) });
  }

  function lightboxPrev(e) {
    e.stopPropagation();
    setLightbox(lb => lb && { ...lb, index: Math.max(lb.index - 1, 0) });
  }

  async function deleteSession(sessionId) {
    if (!window.confirm('Detta raderar sessionen permanent, inklusive alla viner, betyg, kommentarer och kopplade poäng. Kan inte ångras.')) return;
    const session = sessions.find(s => s.id === sessionId);
    const wineIds = session?.wines?.map(w => w.id) || [];
    if (wineIds.length) {
      await nhost.graphql.request(`
        mutation DeleteWineDeps($ids: [uuid!]!) {
          delete_ratings(where: { wine_id: { _in: $ids } }) { affected_rows }
          delete_wine_interests(where: { wine_id: { _in: $ids } }) { affected_rows }
        }
      `, { ids: wineIds });
      await nhost.graphql.request(`
        mutation DeleteWines($ids: [uuid!]!) {
          delete_wines(where: { id: { _in: $ids } }) { affected_rows }
        }
      `, { ids: wineIds });
    }
    await nhost.graphql.request(`
      mutation DeleteSessionData($id: uuid!, $like: String!) {
        delete_user_points(where: { reference_id: { _like: $like } }) { affected_rows }
        delete_session_comments(where: { session_id: { _eq: $id } }) { affected_rows }
        delete_session_photos(where: { session_id: { _eq: $id } }) { affected_rows }
        delete_session_recipes(where: { session_id: { _eq: $id } }) { affected_rows }
      }
    `, { id: sessionId, like: `${sessionId}%` });
    await nhost.graphql.request(`
      mutation DeleteSession($id: uuid!) { delete_sessions_by_pk(id: $id) { id } }
    `, { id: sessionId });
    setAdminMenuOpen(null);
    invalidateCache?.('sessions');
    await fetchSessions(true);
  }

  async function completeSession(sessionId) {
    if (completingSession === sessionId) return;
    setCompletingSession(sessionId);
    try {
      await setSessionFlag(sessionId, 'completed', true);
    } finally {
      setCompletingSession(null);
    }
  }

  function openDetail(wine) { pushBack(() => setDetail(null)); setDetail(wine); }
  function closeDetail() { popBack(); setDetail(null); fetchSessions(); }

  if (detail) return (
    <WineDetail wine={detail} wines={allWines} profile={profile} onBack={closeDetail} onRefresh={fetchSessions} />
  );

  const filteredSessions = sessions.filter(s => {
    const type = s.event?.event_type ?? null;
    if (eventFilter === 'tasting') return type === null || type === 'tasting';
    if (eventFilter === 'wine_box') return type === 'wine_box';
    return true;
  });

  const recentPhotos = sessions
    .flatMap(s => {
      const isHost = s.event?.event_hosts?.some(h => h.profile_id === profile.id) || s.profile?.id === profile.id || profile.is_admin;
      const winesVisible = s.wines_visible || s.completed || isHost;
      return winesVisible ? (s.session_photos || []) : [];
    })
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    .slice(0, 20);

  const divider = { borderTop: '1px solid rgba(60,20,30,.07)' };

  return (
    <div>
      <div className="screen">

        {/* Senaste bilder */}
        {recentPhotos.length > 0 && (
          <div style={{ display: 'flex', gap: 12, overflowX: 'auto', margin: '0 -16px 14px', padding: '2px 16px', scrollbarWidth: 'none' }}>
            {recentPhotos.map((p, i) => (
              <div
                key={p.id}
                onClick={() => openLightbox(recentPhotos.map(x => x.photo_url), i)}
                style={{
                  flexShrink: 0, width: 64, height: 64, borderRadius: '50%',
                  padding: 2, boxSizing: 'border-box',
                  border: '2px solid var(--wine-light)',
                  background: '#fff', cursor: 'pointer',
                }}
              >
                <img
                  src={p.photo_url}
                  alt=""
                  style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover', display: 'block' }}
                />
              </div>
            ))}
          </div>
        )}

        {/* Filter-chips */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          {[{ key: 'tasting', label: 'Provningar' }, { key: 'wine_box', label: 'Vinlåda' }].map(({ key, label }) => {
            const active = eventFilter === key;
            return (
              <span
                key={key}
                onClick={() => setEventFilter(key)}
                style={{
                  display: 'inline-flex', alignItems: 'center',
                  background: active ? 'var(--wine)' : '#fff',
                  color: active ? '#fff' : 'var(--ink)',
                  border: `1px solid ${active ? 'var(--wine)' : 'rgba(139,42,62,.16)'}`,
                  fontSize: 12.5, padding: '8px 16px', borderRadius: 22,
                  cursor: 'pointer', whiteSpace: 'nowrap',
                }}
              >
                {label}
              </span>
            );
          })}
        </div>

        {/* Protokoll-länk */}
        <a
          href="/Vinprotokoll.pdf"
          download="Vinprovningsprotokoll.pdf"
          style={{
            display: 'flex', alignItems: 'center', gap: 11,
            background: '#fff', border: '1px solid rgba(60,20,30,.08)',
            borderRadius: 14, padding: '13px 15px',
            marginBottom: 14, color: 'var(--ink)', textDecoration: 'none',
          }}
        >
          <span style={{ color: 'var(--wine)', display: 'flex' }}>
            <FileText size={22} strokeWidth={1.7} />
          </span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--wine)' }}>Ladda ner provningsprotokoll</div>
            <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>PDF – fyll i under provningen</div>
          </div>
          <span style={{ color: 'var(--muted)', display: 'flex' }}>
            <Download size={18} strokeWidth={1.8} />
          </span>
        </a>

        {!filteredSessions.length && (
          <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--muted)' }}>
            <Wine size={32} color="rgba(60,20,30,.2)" strokeWidth={1.5} style={{ marginBottom: 10 }} />
            <div style={{ fontSize: 14 }}>Inga provningar än. Skapa ett event i kalendern för att komma igång.</div>
          </div>
        )}

        {filteredSessions.map(s => {
          const isHost = s.event?.event_hosts?.some(h => h.profile_id === profile.id) || s.profile?.id === profile.id || profile.is_admin;
          const winesVisible = s.wines_visible || s.completed || isHost;
          const isUpdating = updating === s.id;
          const recipes = s.recipes || [];
          const nr = newRecipe[s.id] || { title: '', url: '' };
          const sessionHosts = s.event?.event_hosts?.length > 0 ? s.event.event_hosts : [{ profile: s.profile }];

          return (
            <div key={s.id} style={{
              background: '#fff',
              border: '1px solid rgba(60,20,30,.08)',
              borderRadius: 18,
              padding: 16,
              marginBottom: 12,
            }}>

              {/* Session-header */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
                <div style={{ display: 'flex', flexShrink: 0 }}>
                  {sessionHosts.map((h, i) => (
                    <div key={h.profile?.id || i} style={{
                      width: 36, height: 36, borderRadius: '50%',
                      background: 'var(--teal-light)', border: '2px solid #fff',
                      marginRight: i < sessionHosts.length - 1 ? -9 : 0,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 14, fontWeight: 600, color: 'var(--teal)',
                    }}>
                      {h.profile?.display_name?.[0]}
                    </div>
                  ))}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontFamily: 'var(--font-display)', fontSize: 17, fontWeight: 600, lineHeight: 1.2 }}>
                    {s.event?.event_type === 'wine_box' ? 'Vinlåda' : 'Provning'} av{' '}
                    {sessionHosts.map((h, i, arr) => (
                      <span key={h.profile?.id || i}>
                        <span style={{ cursor: 'pointer', textDecoration: 'underline', textDecorationStyle: 'dotted' }} onClick={() => onViewMember?.(h.profile?.id)}>
                          {h.profile?.display_name}
                        </span>
                        {i < arr.length - 2 && ', '}
                        {i === arr.length - 2 && ' & '}
                      </span>
                    ))}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 1 }}>
                    {s.event?.event_date ? sweDate(s.event.event_date) : ''}
                    {s.food ? ` · ${s.food}` : ''}
                  </div>
                </div>
              </div>

              {/* Räknare + status */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                <span style={{ fontSize: 12, color: 'var(--muted)' }}>{s.wines.length} vin{s.wines.length !== 1 ? 'er' : ''}</span>
                {s.completed && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'var(--teal)', background: 'var(--teal-light)', padding: '2px 9px', borderRadius: 20 }}>
                    <Check size={11} strokeWidth={2.2} />
                    Avslutad
                  </span>
                )}
                {!s.completed && s.wines_visible && !isHost && (
                  <span style={{ fontSize: 11, color: 'var(--gold)', background: 'var(--gold-light)', padding: '2px 9px', borderRadius: 20 }}>Viner synliga</span>
                )}
              </div>

              {/* Receptlänkar */}
              {recipes.length > 0 && (
                <div style={{ ...divider, paddingTop: 10, marginTop: 10, marginBottom: 6 }}>
                  <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 6 }}>Recept från kvällen</div>
                  {recipes.map(r => (
                    <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                      <a href={r.url} target="_blank" rel="noopener noreferrer"
                        style={{ flex: 1, fontSize: 13, color: 'var(--wine)', textDecoration: 'none' }}>
                        {r.title || r.url}
                      </a>
                      {isHost && (
                        <button onClick={() => deleteRecipe(r.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted)', padding: 2, display: 'flex' }}><X size={14} strokeWidth={1.8} /></button>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Vinlista */}
              {winesVisible ? (
                s.wines.map(w => {
                  const a = avg(w.ratings);
                  const ratingNum = parseFloat(a) || 0;
                  return (
                    <div key={w.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '9px 0', ...divider, cursor: 'pointer' }} onClick={() => openDetail(w)}>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 500 }}>{w.name}</div>
                        <div style={{ fontSize: 11, color: 'var(--muted)' }}>{w.type}{w.vintage ? ` · ${w.vintage}` : ''}</div>
                      </div>
                      <div style={{ flexShrink: 0, marginLeft: 8 }}>
                        {ratingNum > 0 ? <Stars rating={ratingNum} size={13} /> : <span style={{ fontSize: 12, color: 'var(--muted)' }}>–</span>}
                      </div>
                    </div>
                  );
                })
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '16px 0 6px', ...divider, marginTop: 10, color: 'var(--muted)', fontStyle: 'italic', fontSize: 12.5 }}>
                  <Lock size={15} strokeWidth={1.8} />
                  Vinerna är dolda tills provningen avslutas
                </div>
              )}

              {/* Foton */}
              {winesVisible && (
                <div style={{ ...divider, paddingTop: 12, marginTop: 12 }}>
                  {s.session_photos?.length > 0 && (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6, marginBottom: 10 }}>
                      {s.session_photos.map((p, i) => (
                        <div key={p.id} style={{ position: 'relative', aspectRatio: '1', borderRadius: 10, overflow: 'hidden', border: '1px solid rgba(60,20,30,.07)' }}>
                          <img src={p.photo_url} alt="" onClick={() => openLightbox(s.session_photos.map(x => x.photo_url), i)} style={{ width: '100%', height: '100%', objectFit: 'cover', cursor: 'pointer' }} />
                          {(p.user_id === profile.id || profile.is_admin) && (
                            <button onClick={() => deletePhoto(p.id)} style={{ position: 'absolute', top: 4, right: 4, background: 'rgba(0,0,0,0.5)', border: 'none', borderRadius: '50%', width: 22, height: 22, color: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><X size={12} strokeWidth={2} /></button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  {uploadingPhoto === s.id ? (
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 13, color: 'var(--wine)', fontWeight: 500, opacity: 0.5 }}>
                      <Camera size={16} strokeWidth={1.7} />
                      Laddar upp...
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 18 }}>
                      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 13, color: 'var(--wine)', fontWeight: 500, cursor: 'pointer' }}>
                        <ImageIcon size={16} strokeWidth={1.7} />
                        Välj från galleri
                        <input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => handlePhotoFile(s.id, e)} />
                      </label>
                      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 13, color: 'var(--wine)', fontWeight: 500, cursor: 'pointer' }}>
                        <Camera size={16} strokeWidth={1.7} />
                        Ta foto
                        <input type="file" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={e => handlePhotoFile(s.id, e)} />
                      </label>
                    </div>
                  )}
                </div>
              )}

              {/* Kommentarer */}
              {winesVisible && (
                <div style={{ ...divider, paddingTop: 12, marginTop: 12 }}>
                  {s.session_comments?.length > 0 && (
                    <div style={{ marginBottom: 10 }}>
                      {s.session_comments.map(c => (
                        <div key={c.id} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'flex-start' }}>
                          <div style={{ flex: 1 }}>
                            <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--ink)' }}>{c.profile?.display_name} </span>
                            <span style={{ fontSize: 12.5, color: 'var(--muted)' }}>{c.content}</span>
                          </div>
                          {(c.user_id === profile.id || profile.is_admin) && (
                            <button onClick={() => deleteComment(c.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted)', padding: 0, flexShrink: 0, display: 'flex' }}><X size={13} strokeWidth={1.8} /></button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input
                      type="text"
                      value={newComment[s.id] || ''}
                      onChange={e => setNewComment(prev => ({ ...prev, [s.id]: e.target.value }))}
                      onKeyDown={e => e.key === 'Enter' && addComment(s.id)}
                      placeholder="Skriv en kommentar…"
                      style={{ flex: 1, fontSize: 12.5, padding: '9px 12px', border: '1px solid rgba(60,20,30,.12)', borderRadius: 20, fontFamily: 'inherit', background: 'var(--cream)', outline: 'none' }}
                    />
                    <button
                      onClick={() => addComment(s.id)}
                      disabled={!newComment[s.id]?.trim() || addingComment === s.id}
                      style={{ background: 'var(--wine-light)', color: 'var(--wine)', border: 'none', borderRadius: 20, padding: '9px 15px', fontSize: 12, fontWeight: 500, cursor: 'pointer', flexShrink: 0, opacity: !newComment[s.id]?.trim() ? 0.5 : 1, fontFamily: 'inherit' }}
                    >
                      {addingComment === s.id ? '...' : 'Skicka'}
                    </button>
                  </div>
                </div>
              )}

              {/* Swish-betalning */}
              {(() => {
                const hostsWithPhone = (s.event?.event_hosts || []).filter(h => h.profile?.phone);
                if (!hostsWithPhone.length) return null;
                const costRevealed = isHost || s.wines_visible || s.completed;
                if (!costRevealed && !s.event?.cost) return null;
                const selectedPayeeId = swishPayee[s.id] || hostsWithPhone[0].profile_id;
                const payeeHost = hostsWithPhone.find(h => h.profile_id === selectedPayeeId) || hostsWithPhone[0];
                const cost = s.event?.cost ? Math.round(s.event.cost) : null;
                const swishLink = makeSwishLink(payeeHost.profile.phone, cost, `Vinligan provning ${sweDate(s.event?.event_date)}`);
                const goingAttendees = (s.event?.rsvps || []).filter(r => r.status === 'going');
                return (
                  <div style={{ ...divider, paddingTop: 12, marginTop: 12 }}>
                    {isHost ? (
                      <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                        <input
                          type="number"
                          value={editingCost[s.id] || ''}
                          onChange={e => setEditingCost(prev => ({ ...prev, [s.id]: e.target.value }))}
                          placeholder="Pris per person (kr)"
                          style={{ flex: 1, fontSize: 13, padding: '8px 10px', border: '1px solid rgba(60,20,30,.12)', borderRadius: 10, fontFamily: 'inherit', outline: 'none' }}
                        />
                        <button onClick={() => saveCost(s.id, s.event.id)} disabled={savingCost === s.id} style={{ background: 'var(--wine-light)', color: 'var(--wine)', border: 'none', borderRadius: 10, padding: '8px 12px', fontSize: 12, fontWeight: 500, cursor: 'pointer', flexShrink: 0, fontFamily: 'inherit' }}>
                          {savingCost === s.id ? '...' : 'Spara'}
                        </button>
                      </div>
                    ) : costRevealed && cost ? (
                      <div style={{ fontSize: 13, color: 'var(--ink)', marginBottom: 9 }}>{cost} kr/person</div>
                    ) : null}

                    {hostsWithPhone.length > 1 && isHost && (
                      <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
                        {hostsWithPhone.map(h => (
                          <div key={h.profile_id} onClick={() => setSwishPayee(prev => ({ ...prev, [s.id]: h.profile_id }))}
                            style={{ fontSize: 12, padding: '5px 12px', borderRadius: 20, cursor: 'pointer', border: `1px solid ${selectedPayeeId === h.profile_id ? 'var(--wine)' : 'rgba(60,20,30,.12)'}`, background: selectedPayeeId === h.profile_id ? 'var(--wine-light)' : 'white', color: selectedPayeeId === h.profile_id ? 'var(--wine)' : 'var(--muted)', fontWeight: selectedPayeeId === h.profile_id ? 500 : 400 }}>
                            {h.profile.display_name}
                          </div>
                        ))}
                        <div style={{ fontSize: 11, color: 'var(--muted)', width: '100%', marginTop: 2 }}>Välj vem som tar emot betalningen</div>
                      </div>
                    )}

                    {costRevealed && (
                      <a href={swishLink} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, background: '#1F8A5B', color: 'white', border: 'none', borderRadius: 12, padding: 11, fontSize: 14, fontWeight: 600, textDecoration: 'none', cursor: 'pointer' }}>
                        <img src="https://www.swish.nu/img/swish-logo-secondary-light-bg.svg" alt="Swish" style={{ height: 18, filter: 'brightness(0) invert(1)' }} onError={e => e.target.style.display = 'none'} />
                        Betala {payeeHost.profile.display_name}{cost ? ` – ${cost} kr` : ''}
                      </a>
                    )}

                    {costRevealed && goingAttendees.length > 0 && (
                      <div style={{ marginTop: 14, paddingTop: 12, ...divider }}>
                        <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--muted)', marginBottom: 8 }}>Vem har betalat</div>
                        {goingAttendees.map(r => {
                          const payment = (s.payments || []).find(p => p.user_id === r.user_id);
                          const confirmed = !!payment?.confirmed_by_host_at;
                          const selfReported = !!payment?.self_reported_at;
                          const isMe = r.user_id === profile.id;
                          return (
                            <div key={r.user_id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 0', fontSize: 13 }}>
                              <span style={{ color: 'var(--ink)' }}>{r.profile?.display_name || '—'}</span>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span style={{
                                  fontSize: 11, fontWeight: 500, padding: '3px 9px', borderRadius: 20,
                                  background: confirmed ? 'var(--teal-light)' : selfReported ? '#FFF3D6' : 'rgba(60,20,30,.06)',
                                  color: confirmed ? 'var(--teal)' : selfReported ? '#8A6A1F' : 'var(--muted)',
                                }}>
                                  {confirmed ? '✓ Bekräftad' : selfReported ? 'Väntar bekräftelse' : 'Obetald'}
                                </span>
                                {isHost && (
                                  <button
                                    onClick={() => toggleHostConfirm(s.id, r.user_id, !confirmed)}
                                    style={{ fontSize: 11, padding: '3px 9px', borderRadius: 20, border: '1px solid rgba(60,20,30,.12)', background: confirmed ? 'white' : 'var(--teal-light)', color: confirmed ? 'var(--muted)' : 'var(--teal)', cursor: 'pointer', fontFamily: 'inherit' }}
                                  >
                                    {confirmed ? 'Ångra' : 'Bekräfta'}
                                  </button>
                                )}
                                {isMe && !isHost && (
                                  <button
                                    onClick={() => toggleSelfReport(s.id, r.user_id, !selfReported)}
                                    style={{ fontSize: 11, padding: '3px 9px', borderRadius: 20, border: '1px solid rgba(60,20,30,.12)', background: selfReported ? 'white' : 'var(--wine-light)', color: selfReported ? 'var(--muted)' : 'var(--wine)', cursor: 'pointer', fontFamily: 'inherit' }}
                                  >
                                    {selfReported ? 'Ångra' : 'Jag har swishat'}
                                  </button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Värd-kontroller */}
              {isHost && !s.completed && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 14 }}>
                  <div style={{ ...divider, paddingTop: 12 }}>
                    <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink)', marginBottom: 8 }}>Mat & recept</div>
                    <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                      <input type="text" value={editingFood[s.id] || ''} onChange={e => setEditingFood(prev => ({ ...prev, [s.id]: e.target.value }))} placeholder="Vad åts under provningen?" style={{ flex: 1, fontSize: 13, padding: '8px 10px', border: '1px solid rgba(60,20,30,.12)', borderRadius: 10, fontFamily: 'inherit', outline: 'none' }} />
                      <button onClick={() => saveFood(s.id)} disabled={savingFood === s.id} style={{ background: 'var(--wine-light)', color: 'var(--wine)', border: 'none', borderRadius: 10, padding: '8px 12px', fontSize: 12, fontWeight: 500, cursor: 'pointer', flexShrink: 0, fontFamily: 'inherit' }}>
                        {savingFood === s.id ? '...' : 'Spara'}
                      </button>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <input type="text" value={nr.title} onChange={e => setNewRecipe(prev => ({ ...prev, [s.id]: { ...nr, title: e.target.value } }))} placeholder="Receptnamn (valfritt)" style={{ fontSize: 13, padding: '8px 10px', border: '1px solid rgba(60,20,30,.12)', borderRadius: 10, fontFamily: 'inherit', outline: 'none' }} />
                      <div style={{ display: 'flex', gap: 8 }}>
                        <input type="url" value={nr.url} onChange={e => setNewRecipe(prev => ({ ...prev, [s.id]: { ...nr, url: e.target.value } }))} placeholder="https://..." style={{ flex: 1, fontSize: 13, padding: '8px 10px', border: '1px solid rgba(60,20,30,.12)', borderRadius: 10, fontFamily: 'inherit', outline: 'none' }} />
                        <button onClick={() => addRecipe(s.id)} disabled={!nr.url?.trim()} style={{ background: 'var(--wine-light)', color: 'var(--wine)', border: 'none', borderRadius: 10, padding: '8px 12px', fontSize: 12, fontWeight: 500, cursor: 'pointer', flexShrink: 0, opacity: !nr.url?.trim() ? 0.5 : 1, fontFamily: 'inherit' }}>
                          + Lägg till
                        </button>
                      </div>
                    </div>
                  </div>

                  <button onClick={() => { setAddingToSession(s.id); setShowModal(true); }} style={{ width: '100%', padding: 10, border: '1px dashed rgba(139,42,62,.3)', borderRadius: 12, background: 'none', color: 'var(--wine)', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}>
                    + Lägg till vin på denna provning
                  </button>
                  <button disabled={isUpdating} onClick={() => setSessionFlag(s.id, 'wines_visible', !s.wines_visible)} style={{ width: '100%', padding: 12, border: '1px solid rgba(60,20,30,.15)', borderRadius: 12, background: 'none', color: 'var(--muted)', fontSize: 14, cursor: 'pointer', fontFamily: 'inherit' }}>
                    {isUpdating ? '...' : s.wines_visible ? 'Dölj viner för medlemmar' : 'Visa viner för medlemmar'}
                  </button>
                  <button disabled={completingSession === s.id || isUpdating} onClick={() => completeSession(s.id)} style={{ width: '100%', padding: 13, border: 'none', borderRadius: 12, background: 'var(--wine)', color: '#fff', fontSize: 14, fontWeight: 500, cursor: completingSession === s.id ? 'not-allowed' : 'pointer', fontFamily: 'inherit', opacity: completingSession === s.id ? 0.55 : 1 }}>
                    {completingSession === s.id ? 'Avslutar...' : 'Avsluta provning'}
                  </button>
                </div>
              )}

              {isHost && s.completed && (
                <button disabled={isUpdating} onClick={() => setSessionFlag(s.id, 'completed', false)} style={{ width: '100%', marginTop: 14, padding: 12, border: '1px solid rgba(60,20,30,.15)', borderRadius: 12, background: 'none', color: 'var(--muted)', fontSize: 14, cursor: 'pointer', fontFamily: 'inherit' }}>
                  {isUpdating ? '...' : 'Återöppna provning'}
                </button>
              )}

              {profile.is_admin && (
                <div style={{ borderTop: '1px dashed rgba(139,42,62,.12)', marginTop: 14, paddingTop: 10 }}>
                  <button
                    onClick={() => setAdminMenuOpen(adminMenuOpen === s.id ? null : s.id)}
                    style={{ display: 'flex', alignItems: 'center', gap: 5, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted)', fontSize: 11.5, fontWeight: 500, padding: 0, fontFamily: 'inherit' }}
                  >
                    <span style={{ fontSize: 12 }}>⚙</span>
                    Admin
                    <span style={{ fontSize: 9 }}>{adminMenuOpen === s.id ? '▲' : '▼'}</span>
                  </button>
                  {adminMenuOpen === s.id && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 7, marginTop: 10 }}>
                      {s.completed && (
                        <button
                          disabled={isUpdating}
                          onClick={() => { setAdminMenuOpen(null); setSessionFlag(s.id, 'completed', false); }}
                          style={{ background: 'rgba(60,20,30,.04)', border: '1px solid rgba(60,20,30,.1)', borderRadius: 10, padding: '8px 12px', fontSize: 12.5, color: 'var(--ink)', cursor: 'pointer', fontFamily: 'inherit', width: '100%', textAlign: 'left' }}
                        >
                          Ångra "Avsluta provning"
                        </button>
                      )}
                      <button
                        onClick={() => deleteSession(s.id)}
                        style={{ background: 'rgba(185,28,28,.06)', border: '1px solid rgba(185,28,28,.2)', borderRadius: 10, padding: '8px 12px', fontSize: 12.5, color: '#B91C1C', cursor: 'pointer', fontFamily: 'inherit', width: '100%', textAlign: 'left' }}
                      >
                        Radera session
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {lightbox && (
        <div onClick={() => setLightbox(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div onClick={e => e.stopPropagation()} style={{ position: 'relative', maxWidth: '100%', maxHeight: '100%', display: 'flex' }}>
            <img src={lightbox.photos[lightbox.index]} alt="" style={{ maxWidth: '100%', maxHeight: '100%', borderRadius: 8, objectFit: 'contain', display: 'block' }} />
            {lightbox.photos.length > 1 && (
              <>
                <div onClick={lightboxPrev} style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '35%', cursor: lightbox.index > 0 ? 'pointer' : 'default' }} />
                <div onClick={lightboxNext} style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: '35%', cursor: lightbox.index < lightbox.photos.length - 1 ? 'pointer' : 'default' }} />
                <div style={{ position: 'absolute', bottom: 10, left: '50%', transform: 'translateX(-50%)', fontSize: 12, color: 'rgba(255,255,255,.85)', background: 'rgba(0,0,0,.35)', padding: '3px 10px', borderRadius: 12 }}>
                  {lightbox.index + 1} / {lightbox.photos.length}
                </div>
              </>
            )}
          </div>
          <button onClick={() => setLightbox(null)} style={{ position: 'absolute', top: 20, right: 20, background: 'rgba(0,0,0,.4)', border: 'none', borderRadius: '50%', width: 34, height: 34, color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <X size={18} strokeWidth={2} />
          </button>
        </div>
      )}

      {showModal && (
        <WineModal
          profile={profile}
          wines={allWines}
          sessionId={addingToSession}
          onClose={() => { setShowModal(false); setAddingToSession(null); }}
          onSaved={() => { setShowModal(false); setAddingToSession(null); fetchSessions(); }}
        />
      )}
    </div>
  );
}
